import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LicenceForm } from "@/features/compliance/LicenceForm";
import { RenewalHistory } from "@/features/compliance/RenewalHistory";
import { RenewLicenceDialog } from "@/features/compliance/RenewLicenceDialog";
import { LicenceLinkedAssetsSection } from "@/features/compliance/LicenceLinkedAssetsSection";
import { useLicence, useLicenceAlertLog } from "@/features/compliance/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { licenceStatus, daysRemaining, nextAlertDue } from "@/features/compliance/types";
import { LicenceStatusBadge } from "@/features/compliance/badges";
import { fmtDateTime } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/compliance/licences/$licenceId/edit")({
  component: EditLicencePage,
});

const EDITOR_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "quality"]);

function EditLicencePage() {
  const { licenceId } = Route.useParams();
  const { data: me } = useCurrentUser();
  const navigate = useNavigate();
  const { data, isLoading } = useLicence(licenceId);
  const { data: alerts = [] } = useLicenceAlertLog(licenceId);
  const [renewOpen, setRenewOpen] = useState(false);

  if (!me) return null;
  if (!me.roles.some((r) => EDITOR_ROLES.has(r))) {
    return <Card className="mx-auto max-w-xl"><CardContent className="p-6">
      <h2 className="text-lg font-semibold">Permission required</h2>
    </CardContent></Card>;
  }
  if (isLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="py-20 text-center text-muted-foreground">Licence not found.</div>;

  const d = daysRemaining(data.valid_till);
  const s = licenceStatus(data.valid_till);
  const lastAlert = alerts[0]?.sent_at ?? null;
  const sentThresholds = alerts.map((a) => a.threshold);
  const nextDue = nextAlertDue(data.valid_till, sentThresholds);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Edit Licence</h1>
        <Button variant="outline" onClick={() => setRenewOpen(true)}>
          <RefreshCw className="mr-1 h-4 w-4" />Renew
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Alert &amp; Status</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
            <div><div className="text-xs text-muted-foreground">Status</div><div className="mt-1"><LicenceStatusBadge status={s} /></div></div>
            <div><div className="text-xs text-muted-foreground">Days Remaining</div><div className={`mt-1 font-semibold tabular-nums ${d < 0 ? "text-red-600" : d <= 30 ? "text-amber-600" : ""}`}>{d}</div></div>
            <div><div className="text-xs text-muted-foreground">Alert Owner</div><div className="mt-1">{data.alert_owner?.full_name || data.alert_owner?.email || "—"}</div></div>
            <div><div className="text-xs text-muted-foreground">Last Alert Sent</div><div className="mt-1">{lastAlert ? fmtDateTime(lastAlert) : "—"}</div></div>
            <div>
              <div className="text-xs text-muted-foreground">Next Alert Due</div>
              <div className="mt-1">{nextDue === null ? "—" : <Badge variant="secondary">{nextDue === 0 ? "On expiry" : `${nextDue}d threshold`}</Badge>}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <LicenceForm initial={data} onSaved={() => navigate({ to: "/compliance/licences" })} />

      <LicenceLinkedAssetsSection licenceId={licenceId} legacyAssetId={data.asset_id} />

      <RenewalHistory licenceId={licenceId} />

      <RenewLicenceDialog licence={data} open={renewOpen} onOpenChange={setRenewOpen} />
    </div>
  );
}
